import { supabase } from "../supabase-client";
import type { Json } from "../types/database";

export interface UserCredits {
  id: string;
  user_id: string;
  credits_balance: number;
  total_purchased: number;
  total_used: number;
  created_at: string;
  updated_at: string;
}

export interface CreditTransaction {
  id: string;
  user_id: string;
  transaction_type: "purchase" | "usage" | "refund" | "bonus";
  amount: number; // Positive for purchase/refund/bonus, negative for usage
  feature_type?:
    | "document_generation"
    | "contract_generation"
    | "consultation"
    | "case"
    | "exercise"
    | "exam"
    | "simulation"
    | "document_review"
    | "case_summary"
    | "legal_ai_chat";
  stripe_payment_intent_id?: string;
  stripe_charge_id?: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

/**
 * Get user credits balance
 */
export async function getUserCredits(
  userId: string
): Promise<UserCredits | null> {
  const { data, error } = await supabase
    .from("user_credits")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    console.error("Error getting user credits:", error);
    return null;
  }

  // If no record exists, initialize it
  if (!data) {
    try {
      const { error: initError } = await supabase.rpc(
        "initialize_user_credits",
        { p_user_id: userId }
      );

      if (initError) {
        console.error("Error initializing user credits:", initError);
        return null;
      }

      // Fetch the newly created record
      const { data: newData, error: fetchError } = await supabase
        .from("user_credits")
        .select("*")
        .eq("user_id", userId)
        .single();

      if (fetchError) {
        console.error("Error fetching initialized credits:", fetchError);
        return null;
      }

      return newData as UserCredits;
    } catch (err) {
      console.error("Error in getUserCredits:", err);
      return null;
    }
  }

  return data as UserCredits;
}

/**
 * Get user credits balance (number only)
 * Handles both credits_balance (015) and balance (PROD_03) column names
 */
export async function getUserCreditsBalance(userId: string): Promise<number> {
  try {
    const { data, error } = await supabase.rpc("get_user_credits", {
      p_user_id: userId,
    });

    if (!error) {
      return (data as number) ?? 0;
    }
  } catch {
    // RPC may fail if schema differs (e.g. balance vs credits_balance)
  }

  // Fallback: read from table directly (handles both column schemas)
  const { data, error } = await supabase
    .from("user_credits")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();

  if (error || !data) return 0;
  const row = data as { credits_balance?: number; balance?: number };
  return row.credits_balance ?? row.balance ?? 0;
}

/**
 * Purchase credits (add credits to user)
 */
export async function purchaseCredits(
  userId: string,
  amount: number,
  paymentIntentId: string,
  metadata?: Record<string, unknown>
): Promise<UserCredits> {
  const { error } = await supabase.rpc("add_credits", {
    p_user_id: userId,
    p_amount: amount,
    p_transaction_type: "purchase",
    p_stripe_payment_intent_id: paymentIntentId,
    p_metadata: (metadata as Json) || {},
  });

  if (error) {
    console.error("Error purchasing credits:", error);
    throw error;
  }

  // Fetch updated credits
  const credits = await getUserCredits(userId);
  if (!credits) {
    throw new Error("Failed to retrieve credits after purchase");
  }

  return credits;
}

/**
 * Use credits (deduct from balance)
 */
export async function deductCredits(
  userId: string,
  amount: number,
  featureType: CreditTransaction["feature_type"],
  metadata?: Record<string, unknown>
): Promise<number> {
  if (!featureType) {
    throw new Error("Feature type is required to use credits");
  }

  const { data, error } = await supabase.rpc("use_credits", {
    p_user_id: userId,
    p_amount: amount,
    p_feature_type: featureType,
    p_metadata: (metadata as Json) || {},
  });

  if (error) {
    console.error("Error using credits:", error);
    throw error;
  }

  return (data as number) || 0;
}

/**
 * Check if user has enough credits
 */
export async function hasEnoughCredits(
  userId: string,
  amount: number
): Promise<boolean> {
  const { data, error } = await supabase.rpc("has_enough_credits", {
    p_user_id: userId,
    p_amount: amount,
  });

  if (error) {
    console.error("Error checking credits:", error);
    return false;
  }

  return (data as boolean) || false;
}

/**
 * Get credit transactions for a user
 */
export async function getCreditTransactions(
  userId: string,
  limit: number = 50
): Promise<CreditTransaction[]> {
  const { data, error } = await supabase
    .from("credit_transactions")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Error getting credit transactions:", error);
    return [];
  }

  return (data || []) as CreditTransaction[];
}

/**
 * Get credits cost for a feature type
 */
export function getFeatureCreditsCost(
  featureType: CreditTransaction["feature_type"]
): number {
  if (!featureType) return 1;

  const costs: Record<
    NonNullable<CreditTransaction["feature_type"]>,
    number
  > = {
    document_generation: 1,
    contract_generation: 2,
    consultation: 1,
    case: 1,
    exercise: 1,
    exam: 1,
    simulation: 2,
    document_review: 2,
    case_summary: 3,
    legal_ai_chat: 1,
  };

  return costs[featureType] || 1;
}

/**
 * Credit packages for purchase
 */
export interface CreditPackage {
  id: string;
  credits: number;
  price: number; // in USD
  priceId?: string; // Stripe price ID if pre-configured
  popular?: boolean;
}

export const CREDIT_PACKAGES: CreditPackage[] = [
  {
    id: "10",
    credits: 10,
    price: 4.0,
    popular: false,
  },
  {
    id: "25",
    credits: 25,
    price: 9.0,
    popular: true,
  },
  {
    id: "50",
    credits: 50,
    price: 16.0,
    popular: false,
  },
  {
    id: "100",
    credits: 100,
    price: 29.0,
    popular: false,
  },
];

/**
 * Get credit package by ID
 */
export function getCreditPackage(packageId: string): CreditPackage | undefined {
  return CREDIT_PACKAGES.find((pkg) => pkg.id === packageId);
}
